<template>
  <div id="register" class="fill-height justify-center mx-5" tag="section">
    <v-row justify="center">
      <v-col cols="12">
        <v-slide-y-transition appear>
          <v-card class="pa-3 pa-md-5 mx-auto" light>
            <pages-heading class="text-center display-3">School Account Registration</pages-heading>

            <v-row>
              <v-col cols="12" md="6">
                <v-row no-gutters>
                  <v-col v-for="(section, i) in sections" :key="i" cols="12">
                    <v-list-item three-line>
                      <v-list-item-icon class="mr-4 mt-5 mt-md-4">
                        <v-icon
                          :large="$vuetify.breakpoint.mdAndUp"
                          :color="section.iconColor"
                          v-text="section.icon"
                        />
                      </v-list-item-icon>

                      <v-list-item-content>
                        <v-list-item-title
                          class="font-weight-light mb-4 mt-3"
                          v-text="section.title"
                        />

                        <v-list-item-subtitle v-text="section.text" />
                      </v-list-item-content>
                    </v-list-item>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-alert v-if="successMessage" outlined type="success" text>{{successMessage}}</v-alert>

                    <v-alert v-if="errorMessage" type="error" outlined text>{{errorMessage}}</v-alert>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="6">
                <validation-observer ref="observer" v-slot="{ valid }">
                  <base-material-wizard
                    v-model="tab"
                    :available-steps="availableSteps"
                    :items="tabs"
                    class="mx-auto"
                    @click:next="next(valid)"
                    @click:prev="tab--"
                  >
                    <v-tab-item class="pb-12">
                      <form>
                        <div
                          class="text-center display-1 font-weight-bold mb-6"
                        >Country, Region or State and District where your school is situated.</div>

                        <v-row class="mx-auto" justify="space-around">
                          <v-col cols="12">
                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                              name="country"
                            >
                              <v-select
                                v-model="country"
                                :items="countryNames"
                                item-text="title"
                                item-value="id"
                                label="Select Country"
                                outlined
                                :error-messages="errors"
                              ></v-select>
                            </validation-provider>

                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                              name="regionOrState"
                            >
                              <v-select
                                v-model="regionOrState"
                                :items="regionsOrStates"
                                label="Select Region or State"
                                outlined
                                :error-messages="errors"
                              ></v-select>
                            </validation-provider>

                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                              name="district"
                            >
                              <v-text-field
                                v-model="district"
                                :error-messages="errors"
                                color="secondary"
                                label="District*"
                                outlined
                                prepend-icon="mdi-map-marker"
                                validate-on-blur
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </form>
                    </v-tab-item>

                    <v-tab-item class="pb-12">
                      <form>
                        <v-responsive class="mx-auto">
                          <div
                            class="text-center display-1 font-weight-bold mb-6"
                          >Brief information about your school</div>

                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="schoolType"
                          >
                            <v-select
                              v-model="schoolType"
                              :items="schoolTypes"
                              item-text="name"
                              item-value="code"
                              label="Select Type of School"
                              outlined
                              :error-messages="errors"
                            ></v-select>
                          </validation-provider>

                          <validation-provider v-slot="{ errors }" rules="required" name="code">
                            <v-text-field
                              v-model="code"
                              :error-messages="errors"
                              color="secondary"
                              label="Accreditation Number or Registration Code*"
                              hint="Accreditation number or registration code supplied by a national accreditation board."
                              persistent-hint
                              outlined
                              validate-on-blur
                            />
                          </validation-provider>

                          <validation-provider v-slot="{ errors }" rules="required" name="name">
                            <v-text-field
                              v-model="name"
                              :error-messages="errors"
                              color="secondary"
                              label="Name of the School*"
                              hint="Please enter the name of your school as registered with your national accreditation board."
                              persistent-hint
                              outlined
                              validate-on-blur
                            />
                          </validation-provider>
                        </v-responsive>
                      </form>
                    </v-tab-item>

                    <v-tab-item class="pb-12 text-capitalize">
                      <div
                        class="text-center display-1 font-weight-bold mb-6"
                      >Personal details of the System Administrator.</div>

                      <form>
                        <v-row class="mx-auto" justify="space-around">
                          <v-col cols="12">
                            <v-select v-model="title" :items="titles" label="Select Title" outlined></v-select>
                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                              name="firstname"
                            >
                              <v-text-field
                                v-model="firstname"
                                :error-messages="errors"
                                color="secondary"
                                label="Firstname*"
                                outlined
                                validate-on-blur
                              />
                            </validation-provider>

                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                              name="surname"
                            >
                              <v-text-field
                                v-model="surname"
                                :error-messages="errors"
                                color="secondary"
                                label="Surname*"
                                outlined
                                validate-on-blur
                              />
                            </validation-provider>
                            <v-text-field
                              v-model="othernames"
                              color="secondary"
                              label="Other Names"
                              outlined
                            />
                            <validation-provider
                              v-slot="{ errors }"
                              rules="required|numeric|max:10"
                              name="phone"
                            >
                              <v-text-field
                                v-model="phone"
                                :error-messages="errors"
                                color="secondary"
                                label="Phone Number*"
                                prepend-icon="mdi-phone"
                                :hint="'Enter phone number without country code or leading zero ' + phoneHint"
                                persistent-hint
                                validate-on-blur
                                outlined
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </form>
                    </v-tab-item>

                    <v-tab-item class="pb-12">
                      <div
                        class="text-center display-1 font-weight-bold mb-6"
                      >Signing in credentials of the System Administrator.</div>

                      <v-form ref="credform">
                        <v-row class="mx-auto" justify="space-around">
                          <v-col cols="12">
                            <validation-provider
                              v-slot="{ errors }"
                              rules="required|email"
                              name="email"
                            >
                              <v-text-field
                                v-model="email"
                                :error-messages="errors"
                                color="secondary"
                                label="Email Address*"
                                prepend-icon="mdi-email"
                                hint="Email address will be the username for Adminstrator"
                                persistent-hint
                                validate-on-blur
                                outlined
                              />
                            </validation-provider>
                            <validation-provider
                              v-slot="{ errors }"
                              name="password"
                              rules="required"
                            >
                              <v-text-field
                                v-model="password"
                                class="mb-8"
                                label="Password*"
                                :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="show ? 'text' : 'password'"
                                color="secondary"
                                @click:append.prevent="show = !show"
                                prepend-icon="mdi-lock-outline"
                                :error-messages="errors"
                                hint="Please choose a secure mixed cased password which is hard to guess"
                                persistent-hint
                                outlined
                              />
                            </validation-provider>
                            <validation-provider
                              v-slot="{ errors }"
                              name="confirmPassword"
                              rules="required"
                            >
                              <v-text-field
                                v-model="confirmPassword"
                                class="mb-8"
                                label="Confirm Password*"
                                :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                                :type="showConfirm ? 'text' : 'password'"
                                color="secondary"
                                @click:append.prevent="showConfirm = !showConfirm"
                                prepend-icon="mdi-lock-outline"
                                :error-messages="errors"
                                hint="Enter the password again. We want to make sure you remember what you typed."
                                persistent-hint
                                outlined
                              />
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-form>
                    </v-tab-item>

                    <v-tab-item class="pb-12">
                      <form>
                        <v-responsive class="mx-auto">
                          <div
                            class="text-center display-1 font-weight-bold mb-6"
                          >Choose the subscription package you wish to try out.</div>

                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="subscriptionType"
                          >
                            <v-select
                              v-model="subscriptionType"
                              :items="subscriptionTypes"
                              item-text="name"
                              item-value="code"
                              label="Select Subscription Package"
                              outlined
                              :error-messages="errors"
                            ></v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="paymentPlan"
                          >
                            <v-select
                              v-model="paymentPlan"
                              :items="paymentPlans"
                              item-text="name"
                              item-value="code"
                              label="Select Payment Plan"
                              outlined
                              :error-messages="errors"
                            ></v-select>
                          </validation-provider>

                          <v-switch
                            v-model="isTrialMode"
                            hide-details
                            label="Start 15-day free trial on selected package."
                          />
                        </v-responsive>
                      </form>
                    </v-tab-item>
                  </base-material-wizard>
                </validation-observer>
              </v-col>
            </v-row>
          </v-card>
        </v-slide-y-transition>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PagesRegister",

  components: {
    // PagesBtn: () => import("./components/Btn"),
    PagesHeading: () => import("./components/Heading")
  },

  data: () => ({
    loading: false,
    errorMessage: null,
    successMessage: null,
    // titles
    titles: ["Mr", "Mrs", "Dr", "Prof", "Rev"],
    // school info
    code: "",
    name: "",
    // country info
    country: null,
    regionOrState: null,
    district: "",
    // admin user info
    email: "",
    phone: "",
    password: "",
    show: false,
    confirmPassword: "",
    showConfirm: false,
    title: "",
    firstname: "",
    othernames: "",
    surname: "",

    // school account
    schoolType: null,
    subscriptionType: null,
    paymentPlan: null,
    isTrialMode: false,

    agreedTerms: false,

    tab: 0,
    tabs: [
      "Country",
      "School Information",
      "Personal Details",
      "Sign-In Details",
      "Subscription Package"
    ],

    sections: [
      {
        icon: "mdi-chart-timeline-variant",
        iconColor: "primary",
        title: "Manage records securely online with full audit trail.",
        text:
          "School 360 Online's Student Information Management System (SIMS) helps schools manage school records securely online with full audit trail."
      },
      {
        icon: "mdi-code-tags",
        iconColor: "secondary",
        title: "User friendly SIMS platform",
        text:
          "Simple user experience designed to help school administrators and managers focus on educating their students."
      },
      {
        icon: "mdi-barcode-scan",
        iconColor: "cyan",
        title: "Digital student records, paper-free school",
        text:
          "Scan historical student records, catalogue and index digitized records on a secure cloud platform."
      },
      {
        icon: "mdi-account-multiple",
        iconColor: "cyan",
        title: "Realtime dashboards, accounts and stock management.",
        text:
          "Realtime dashboards, trend and performance analysis. Bursary activities, requisition and inventory management"
      }
    ]
  }),
  methods: {
    readCountryData() {
      this.loading = true;
      this.$store
        .dispatch("countryDataService/getCountries")
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    resetRegistration() {
      // this.$v.$reset();
      this.code = null;
      this.name = null;
      this.district = null;
      this.regionOrState = null;
      this.schoolType = null;
      this.subscriptionType = null;
      this.paymentPlan = null;
      this.isTrialMode = false;
      this.country = null;
      this.email = null;
      this.phone = null;
      this.password = null;
      this.title = null;
      this.firstname = null;
      this.othernames = null;
      this.surname = null;
      this.$refs.observer.reset();
    },
    next(valid) {
      if (!valid) return;

      if (this.tab === this.tabs.length - 1) {
        this.loading = true;
        this.errorMessage = null;
        this.successMessage = null;

        const school = {
          code: this.code,
          name: this.name,
          district: this.district,
          regionOrState: this.regionOrState,
          country: this.country,
          adminAccount: {
            emailAddress: this.email,
            phoneNumber: this.getPhoneNumber,
            password: this.password,
            title: this.title,
            firstname: this.firstname,
            otherNames: this.othernames,
            surname: this.surname
          },
          schoolAccount: {
            schoolType: this.schoolType,
            subscriptionType: this.subscriptionType,
            paymentPlan: this.paymentPlan,
            isTrialMode: this.isTrialMode
          }
        };

        //alert(JSON.stringify(school));

        this.$store
          .dispatch(
            "schoolRegistrationService/registerSchool",
            JSON.stringify(school)
          )
          .then(response => {
            this.loading = false;
            this.successMessage = JSON.stringify(response.data);
            this.resetRegistration();
            // this.$router.push("/");
          })
          .catch(error => {
            this.loading = false;
            // console.log(error.message);
            this.errorMessage = error.message;
          });
      } else {
        this.tab++;
      }
    }
  },
  computed: {
    ...mapGetters("countryDataService", {
      countryNames: "countryNamesOnly"
    }),
    ...mapGetters("appDataService", {
      schoolTypes: "schoolTypes",
      subscriptionTypes: "subscriptionTypes",
      paymentPlans: "paymentPlans"
    }),
    regionsOrStates() {
      var regions = [];
      if (this.country !== null) {
        regions = this.$store.getters["countryDataService/country"](
          this.country
        ).data.regionsOrStates;
      }

      return regions;
    },
    countryCode() {
      var ccode = "";
      if (this.country !== null) {
        ccode = this.$store.getters["countryDataService/country"](this.country)
          .data.countryCode;
      }
      return ccode;
    },

    phoneHint() {
      return this.countryCode + " " + this.phone;
    },
    getPhoneNumber() {
      return this.countryCode + "" + this.phone;
    },
    availableSteps() {
      const steps = [0];

      if (this.country && this.regionOrState && this.district) steps.push(1);

      if (this.code && this.name && this.schoolType && steps.includes(1))
        steps.push(2);

      if (this.phone && this.firstname && this.surname && steps.includes(2))
        steps.push(3);

      if (this.email && this.password && steps.includes(3)) {
        steps.push(4);
      }

      if (this.subscriptionType && this.paymentPlan && steps.includes(4)) {
        steps.push(5);
      }

      return steps;
    }
  },
  created() {
    this.readCountryData();
  }
};
</script>

<style lang="sass">
#register
  .v-list-item__subtitle
    -webkic-line-clamp: initial
    -webkit-box-orient: initial
</style>
